import React, { Fragment, useEffect, useMemo, useState } from "react";
import axios from "axios";
import swal from "sweetalert";

const API_KEY = "eyJ0eXAiOiJKV1Qidfg%!#%$%LCdf#*&*JhbGciOiJSUzI1NiJ9";

const EMPTY_SUMMARY = {
  installment_total: 0,
  installment_amount: 0,
  booking_fee: 0,
};

const Checkout = () => {
  const selectedCurrency = 'BDT';
  const authToken = localStorage.getItem("auth_token");

  const [projects, setProjects] = useState([]);
  const [projectTypes, setProjectTypes] = useState([]);
  const [installmentTypes, setInstallmentTypes] = useState([]);
  const [summary, setSummary] = useState(null);

  const [selectedProductId, setSelectedProductId] = useState("");
  const [selectedTypeOfPlot, setSelectedTypeOfPlot] = useState("");
  const [landQuantity, setLandQuantity] = useState("3");
  const [plotNumber, setPlotNumber] = useState("");
  const [plotCheckStatus, setPlotCheckStatus] = useState("idle");
  const [plotCheckMessage, setPlotCheckMessage] = useState("");
  const [selectedPaymentType, setSelectedPaymentType] = useState("");
  const [selectedInstallment, setSelectedInstallment] = useState("");

  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [errors, setErrors] = useState({});

  const [loadingProjects, setLoadingProjects] = useState(true);
  const [loadingProjectTypes, setLoadingProjectTypes] = useState(false);
  const [loadingPaymentData, setLoadingPaymentData] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const requestData = (extra = {}) => ({
    key_s: API_KEY,
    currency: selectedCurrency,
    ...extra,
  });

  const showRequestError = (error, fallback = "Something went wrong. Please try again.") => {
    const message =
      error?.response?.data?.message ||
      error?.response?.data?.error ||
      fallback;
    swal("Warning", message, "warning");
  };

  const money = (value) => {
    const number = Number(value || 0);
    return new Intl.NumberFormat("en-BD", {
      maximumFractionDigits: 2,
    }).format(Number.isFinite(number) ? number : 0);
  };

  useEffect(() => {
    let active = true;

    const fetchProjects = async () => {
      if (!authToken) {
        setLoadingProjects(false);
        return;
      }

      try {
        setLoadingProjects(true);
        const response = await axios.post("/apps-project", requestData());
        if (active) setProjects(Array.isArray(response?.data?.data) ? response.data.data : []);
      } catch (error) {
        if (active) showRequestError(error, "Unable to load projects.");
      } finally {
        if (active) setLoadingProjects(false);
      }
    };

    fetchProjects();
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    let active = true;

    setSelectedTypeOfPlot("");
    setLandQuantity("3");
    setSelectedPaymentType("");
    setSelectedInstallment("");
    setProjectTypes([]);
    setInstallmentTypes([]);
    setSummary(null);
    setErrors({});

    if (!selectedProductId || !authToken) return undefined;

    const fetchProjectTypes = async () => {
      try {
        setLoadingProjectTypes(true);
        const response = await axios.post(
          "/apps-project-type",
          requestData({ product_id: selectedProductId })
        );
        if (active) {
          setProjectTypes(Array.isArray(response?.data?.data) ? response.data.data : []);
        }
      } catch (error) {
        if (active) showRequestError(error, "Unable to load plot types.");
      } finally {
        if (active) setLoadingProjectTypes(false);
      }
    };

    fetchProjectTypes();
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProductId]);

  useEffect(() => {
    setSelectedPaymentType("");
    setSelectedInstallment("");
    setInstallmentTypes([]);
    setSummary(null);
    setErrors({});
  }, [selectedTypeOfPlot]);

  useEffect(() => {
    let active = true;

    setSelectedInstallment("");
    setInstallmentTypes([]);
    setSummary(null);
    setErrors({});

    if (!selectedPaymentType || !selectedProductId || !selectedTypeOfPlot) return undefined;

    const fetchPaymentData = async () => {
      try {
        setLoadingPaymentData(true);

        if (selectedPaymentType === "installment") {
          const response = await axios.post(
            "/apps-payment-type",
            requestData({
              payment_type: selectedPaymentType,
              selectedProductId,
            })
          );

          if (active) {
            setInstallmentTypes(Array.isArray(response?.data?.data) ? response.data.data : []);
          }
        } else {
          const response = await axios.post(
            "/packege-one-select",
            requestData({
              payment_type: selectedPaymentType,
              selectedProductId,
            })
          );

          if (active) {
            setSummary(response?.data?.data || null);
          }
        }
      } catch (error) {
        if (active) showRequestError(error, "Unable to load payment information.");
      } finally {
        if (active) setLoadingPaymentData(false);
      }
    };

    fetchPaymentData();
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPaymentType, selectedProductId, selectedTypeOfPlot]);

  useEffect(() => {
    let active = true;

    setSummary(null);
    setErrors({});

    if (
      selectedPaymentType !== "installment" ||
      !selectedInstallment ||
      !selectedProductId ||
      !selectedTypeOfPlot ||
      !authToken
    ) {
      return undefined;
    }

    const fetchInstallmentSummary = async () => {
      try {
        setLoadingPaymentData(true);
        const response = await axios.post(
          "/apps-installment-amount",
          requestData({
            selectedProductId,
            selectedPaymentTyp: selectedPaymentType,
            selectedTypeOfPlot,
            selectedsetInstallment: selectedInstallment,
          })
        );

        if (active) setSummary(response?.data?.data || null);
      } catch (error) {
        if (active) showRequestError(error, "Unable to calculate installment information.");
      } finally {
        if (active) setLoadingPaymentData(false);
      }
    };

    fetchInstallmentSummary();
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedInstallment]);

  // Live plot-number verification. Empty is allowed because Plot Number is optional.
  useEffect(() => {
    const value = plotNumber.trim();
    let active = true;

    if (!value) {
      setPlotCheckStatus("idle");
      setPlotCheckMessage("");
      return undefined;
    }

    setPlotCheckStatus("checking");
    setPlotCheckMessage("Checking plot number...");

    const timer = setTimeout(async () => {
      try {
        const response = await axios.post(
          "/check-plot-number",
          requestData({ plot_number: value })
        );

        if (!active) return;
        const isValid = response?.data?.valid === true || response?.data?.data?.valid === true;
        setPlotCheckStatus(isValid ? "valid" : "invalid");
        setPlotCheckMessage(
          response?.data?.message || (isValid ? "Valid plot number." : "Invalid plot number.")
        );
      } catch (error) {
        if (!active) return;
        setPlotCheckStatus("error");
        setPlotCheckMessage(
          error?.response?.data?.message || "Unable to verify this plot number. Please try again."
        );
      }
    }, 400);

    return () => {
      active = false;
      clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plotNumber]);

  const summaryData = summary || EMPTY_SUMMARY;
  const quantityNumber = Number(landQuantity);
  const allowedLandAreas = [3, 5, 9, 12, 15];
  const validQuantity = allowedLandAreas.includes(quantityNumber);

  // Existing APIs provide the price for ONE katha.
  // Keep original API response unchanged and multiply only the displayed values.
  const quantityMultiplier = validQuantity ? quantityNumber : 0;
  const calculatedSummary = {
    booking_fee: Number(summaryData.booking_fee || 0) * quantityMultiplier,
    installment_total: Number(summaryData.installment_total || 0) * quantityMultiplier,
    installment_amount: Number(summaryData.installment_amount || 0) * quantityMultiplier,
  };

  const updateLandQuantity = (nextValue) => {
    setLandQuantity(String(nextValue));
    setErrors((prev) => ({ ...prev, land_quantity: undefined }));
  };

  const payableAmount = useMemo(() => {
    const booking = calculatedSummary.booking_fee;
    if (booking > 0) return booking;

    const installment = calculatedSummary.installment_amount;
    if (installment > 0) return installment;

    return calculatedSummary.installment_total;
  }, [calculatedSummary.booking_fee, calculatedSummary.installment_amount, calculatedSummary.installment_total]);

  const canSubmit =
    !!authToken &&
    !!selectedProductId &&
    !!selectedTypeOfPlot &&
    validQuantity &&
    !!selectedPaymentType &&
    (selectedPaymentType !== "installment" || !!selectedInstallment) &&
    !!summary &&
    !!password.trim() &&
    acceptedTerms &&
    !submitting &&
    !loadingPaymentData &&
    ( !plotNumber.trim() || plotCheckStatus === "valid" );

  const validateBeforeSubmit = () => {
    const nextErrors = {};

    if (!selectedProductId) nextErrors.selectedProductId = "Please select a project.";
    if (!selectedTypeOfPlot) nextErrors.selectedTypeOfPlot = "Please select a plot type.";
    if (!validQuantity) nextErrors.land_quantity = "Please select one of the available land areas: 3, 5, 9, 12 or 15 Kathas.";
    if (!selectedPaymentType) nextErrors.selectedPaymentType = "Please select a payment type.";
    if (selectedPaymentType === "installment" && !selectedInstallment) {
      nextErrors.selectedsetInstallment = "Please select an installment plan.";
    }
    if (!password.trim()) nextErrors.password = "Account password is required.";
    if (!acceptedTerms) nextErrors.terms = "Please accept the terms & conditions.";
    if (!summary) nextErrors.amount = "Please complete all booking selections first.";
    if (plotNumber.trim() && plotCheckStatus !== "valid") {
      nextErrors.plot_number = plotCheckStatus === "checking"
        ? "Please wait while the plot number is being verified."
        : "Please enter a valid plot number.";
    }

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const depositSubmit = async (e) => {
    e.preventDefault();

    if (!validateBeforeSubmit() || submitting) return;

    if (!authToken) {
      swal("Warning", "Your login session is missing. Please login again.", "warning");
      return;
    }

    // Send all calculated values; backend must recalculate them from database rates.
    const data = {
      amount: calculatedSummary.booking_fee, // legacy field
      booking_money: calculatedSummary.booking_fee,
      total_amount: calculatedSummary.installment_total,
      installment_amount: calculatedSummary.installment_amount,
      land_quantity: quantityNumber,
      plot_number: plotNumber.trim(), // Optional plot number
      password,
      currency: selectedCurrency,
      selectedProductId,
      selectedPaymentTyp: selectedPaymentType,
      selectedTypeOfPlot,
      selectedsetInstallment: selectedInstallment || null,
      key_s: API_KEY,
    };

    try {
      setSubmitting(true);
      setErrors({});

      const res = await axios.post("/land-booking", data);

      if (res?.data?.status === "success") {
        await swal("Success", res.data.message || "Booking completed successfully.", "success");
        setPassword("");
        setAcceptedTerms(false);
      } else if (res?.data?.status === "fail") {
        swal("Warning", res.data.message || "Booking could not be completed.", "warning");
      } else {
        setErrors(res?.data?.validation_error || {});
      }
    } catch (error) {
      if (error?.response?.status === 422) {
        setErrors(error?.response?.data?.errors || error?.response?.data?.validation_error || {});
      } else {
        showRequestError(error, "Booking request failed. Please try again.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  const paymentOptions = [
    { value: "one_time", title: "One Time", subtitle: "Pay the full amount at once", icon: "✓" },
    { value: "installment", title: "Installment", subtitle: "Pay with an installment plan", icon: "≡" },
    { value: "down_pay", title: "Down Pay", subtitle: "Start with a down payment", icon: "↓" },
  ];

  return (
    <Fragment>
      <style>{`
        .smart-checkout {
          --sc-primary: #f4b400;
          --sc-primary-dark: #d99b00;
          --sc-ink: #172033;
          --sc-muted: #758096;
          --sc-border: #e8ebf2;
          --sc-bg: #f7f8fc;
          --sc-success: #1b9c5a;
          font-family: inherit;
          color: var(--sc-ink);
        }
        .smart-checkout * { box-sizing: border-box; }
        .smart-checkout .checkout-shell {
          background: var(--sc-bg);
          border-radius: 24px;
          padding: 24px;
        }
        .smart-checkout .hero-card {
          position: relative;
          overflow: hidden;
          background: linear-gradient(135deg, #171d2c 0%, #242d43 100%);
          border: 0;
          border-radius: 22px;
          padding: 28px;
          margin-bottom: 22px;
          box-shadow: 0 18px 45px rgba(20, 28, 45, .12);
        }
        .smart-checkout .hero-card:after {
          content: "";
          position: absolute;
          width: 220px;
          height: 220px;
          right: -80px;
          top: -100px;
          border-radius: 50%;
          background: rgba(244,180,0,.16);
        }
        .smart-checkout .hero-eyebrow {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          color: #ffd96a;
          font-size: 12px;
          font-weight: 800;
          letter-spacing: .12em;
          text-transform: uppercase;
          margin-bottom: 8px;
        }
        .smart-checkout .hero-title {
          color: #fff;
          font-size: clamp(24px, 4vw, 36px);
          font-weight: 800;
          line-height: 1.15;
          margin: 0 0 8px;
        }
        .smart-checkout .hero-text {
          color: rgba(255,255,255,.68);
          max-width: 680px;
          margin: 0;
          font-size: 15px;
        }
        .smart-checkout .section-card,
        .smart-checkout .summary-card {
          background: #fff;
          border: 1px solid var(--sc-border);
          border-radius: 20px;
          box-shadow: 0 10px 30px rgba(26, 36, 58, .05);
        }
        .smart-checkout .section-card { padding: 22px; margin-bottom: 18px; }
        .smart-checkout .section-head {
          display: flex;
          align-items: center;
          gap: 12px;
          margin-bottom: 18px;
        }
        .smart-checkout .step-number {
          width: 36px;
          height: 36px;
          border-radius: 12px;
          background: #fff5d6;
          color: #a87500;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          font-weight: 900;
          flex: 0 0 36px;
        }
        .smart-checkout .section-title {
          margin: 0;
          font-size: 18px;
          font-weight: 800;
          color: var(--sc-ink);
        }
        .smart-checkout .section-subtitle {
          color: var(--sc-muted);
          font-size: 13px;
          margin-top: 2px;
        }
        .smart-checkout .choice-grid {
          display: grid;
          grid-template-columns: repeat(3, minmax(0, 1fr));
          gap: 14px;
        }
        .smart-checkout .choice-card {
          position: relative;
          min-height: 118px;
          border: 1.5px solid var(--sc-border);
          border-radius: 17px;
          padding: 18px;
          cursor: pointer;
          background: #fff;
          transition: all .2s ease;
          display: flex;
          flex-direction: column;
          justify-content: center;
          user-select: none;
        }
        .smart-checkout .choice-card:hover {
          transform: translateY(-2px);
          border-color: #d7dce7;
          box-shadow: 0 10px 24px rgba(28, 38, 60, .08);
        }
        .smart-checkout .choice-card.active {
          border-color: var(--sc-primary);
          background: #fffaf0;
          box-shadow: 0 8px 24px rgba(244,180,0,.12);
        }
        .smart-checkout .choice-card input {
          position: absolute;
          opacity: 0;
          pointer-events: none;
        }
        .smart-checkout .choice-check {
          position: absolute;
          right: 14px;
          top: 14px;
          width: 22px;
          height: 22px;
          border-radius: 50%;
          border: 2px solid #d7dce5;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 12px;
          font-weight: 900;
        }
        .smart-checkout .choice-card.active .choice-check {
          background: var(--sc-primary);
          border-color: var(--sc-primary);
          color: #202020;
        }
        .smart-checkout .choice-index,
        .smart-checkout .choice-icon {
          width: 34px;
          height: 34px;
          border-radius: 11px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          background: #f3f5f9;
          color: #68748a;
          font-weight: 900;
          margin-bottom: 12px;
        }
        .smart-checkout .choice-card.active .choice-index,
        .smart-checkout .choice-card.active .choice-icon {
          background: #ffe59a;
          color: #6d5000;
        }
        .smart-checkout .choice-title {
          font-size: 15px;
          line-height: 1.35;
          font-weight: 800;
          color: var(--sc-ink);
          padding-right: 24px;
        }
        .smart-checkout .choice-subtitle {
          margin-top: 5px;
          color: var(--sc-muted);
          font-size: 12px;
        }
        .smart-checkout .loading-box,
        .smart-checkout .empty-box {
          padding: 18px;
          border: 1px dashed #d9dee8;
          border-radius: 14px;
          color: var(--sc-muted);
          text-align: center;
          background: #fafbfc;
        }
        .smart-checkout .summary-card {
          padding: 22px;
          position: sticky;
          top: 20px;
        }
        .smart-checkout .summary-title {
          font-size: 18px;
          font-weight: 800;
          margin: 0 0 4px;
        }
        .smart-checkout .summary-subtitle {
          color: var(--sc-muted);
          font-size: 13px;
          margin-bottom: 18px;
        }
        .smart-checkout .summary-row {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 15px;
          padding: 13px 0;
          border-bottom: 1px solid #eff1f5;
        }
        .smart-checkout .summary-row:last-of-type { border-bottom: 0; }
        .smart-checkout .summary-label { color: var(--sc-muted); font-size: 13px; }
        .smart-checkout .summary-value { font-weight: 800; text-align: right; }
        .smart-checkout .payable-box {
          margin-top: 15px;
          background: #171d2c;
          border-radius: 16px;
          padding: 16px;
          color: #fff;
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 15px;
        }
        .smart-checkout .payable-box small { color: rgba(255,255,255,.6); display: block; }
        .smart-checkout .payable-box strong { font-size: 20px; color: #ffd45b; }
        .smart-checkout .field-label {
          display: block;
          font-size: 13px;
          font-weight: 800;
          color: #3b4558;
          margin-bottom: 7px;
        }
        .smart-checkout .smart-input {
          width: 100%;
          height: 48px;
          border: 1.5px solid #e1e5ed;
          border-radius: 13px;
          padding: 0 14px;
          outline: none;
          font-size: 14px;
          background: #fff;
          transition: .2s ease;
        }
        .smart-checkout .smart-input:focus {
          border-color: var(--sc-primary);
          box-shadow: 0 0 0 4px rgba(244,180,0,.10);
        }
        .smart-checkout .smart-input[readonly] { background: #f7f8fa; color: #626d80; }
        .smart-checkout .password-wrap { position: relative; }
        .smart-checkout .password-wrap .smart-input { padding-right: 74px; }
        .smart-checkout .password-toggle {
          position: absolute;
          right: 8px;
          top: 50%;
          transform: translateY(-50%);
          border: 0;
          background: #f0f2f6;
          color: #4f5a6d;
          font-weight: 700;
          border-radius: 9px;
          padding: 7px 10px;
          cursor: pointer;
        }
        .smart-checkout .field-error {
          color: #d83a4b;
          font-size: 12px;
          margin-top: 6px;
          display: block;
        }
        .smart-checkout .terms-box {
          display: flex;
          gap: 10px;
          align-items: flex-start;
          background: #fafbfc;
          border: 1px solid var(--sc-border);
          border-radius: 13px;
          padding: 13px 14px;
          margin: 16px 0;
        }
        .smart-checkout .terms-box input { margin-top: 3px; accent-color: var(--sc-primary); }
        .smart-checkout .terms-box label { margin: 0; color: #536075; font-size: 13px; cursor: pointer; }
        .smart-checkout .submit-btn {
          width: 100%;
          min-height: 52px;
          border: 0;
          border-radius: 14px;
          background: linear-gradient(135deg, #ffc928, #f1ad00);
          color: #241b00;
          font-size: 15px;
          font-weight: 900;
          cursor: pointer;
          box-shadow: 0 10px 22px rgba(244,180,0,.20);
          transition: .2s ease;
        }
        .smart-checkout .submit-btn:hover:not(:disabled) { transform: translateY(-1px); }
        .smart-checkout .submit-btn:disabled {
          background: #e4e7ed;
          color: #9aa2b1;
          box-shadow: none;
          cursor: not-allowed;
        }
        .smart-checkout .login-warning {
          background: #fff4e5;
          color: #925f00;
          border: 1px solid #ffe0a8;
          border-radius: 13px;
          padding: 12px 14px;
          margin-bottom: 16px;
          font-size: 13px;
        }
        /* Land quantity picker */
        .smart-checkout .land-quantity-panel {
          border: 1px solid #e7eaf0;
          background: #fafbfe;
          border-radius: 16px;
          padding: 18px;
        }
        .smart-checkout .land-quantity-input-wrap {
          display: flex;
          align-items: center;
          background: #fff;
          border: 1.5px solid #dfe4ec;
          border-radius: 13px;
          overflow: hidden;
          max-width: 360px;
        }
        .smart-checkout .land-quantity-input-wrap:focus-within {
          border-color: var(--sc-primary);
          box-shadow: 0 0 0 4px rgba(244,180,0,.10);
        }
        .smart-checkout .land-quantity-input {
          width: 100%;
          min-width: 0;
          height: 54px;
          padding: 0 15px;
          border: 0;
          outline: 0;
          font-size: 20px;
          font-weight: 800;
          color: var(--sc-ink);
          background: transparent;
        }
        .smart-checkout .land-unit {
          padding: 0 16px;
          color: #69758a;
          font-size: 13px;
          font-weight: 800;
          white-space: nowrap;
        }
        .smart-checkout .land-presets {
          display: flex;
          flex-wrap: wrap;
          gap: 9px;
          margin-top: 14px;
        }
        .smart-checkout .land-preset {
          border: 1px solid #dce2eb;
          border-radius: 10px;
          background: #fff;
          padding: 10px 15px;
          font-size: 13px;
          font-weight: 800;
          color: #526075;
          cursor: pointer;
        }
        .smart-checkout .land-preset.active {
          background: #fff3ca;
          border-color: var(--sc-primary);
          color: #785600;
        }
        .smart-checkout .land-hint {
          color: var(--sc-muted);
          font-size: 12px;
          margin-top: 10px;
        }
 
       .smart-checkout .single-project-grid { grid-template-columns: minmax(0, 1fr); }
       .smart-checkout .single-project-card {
         width: 100%;
         min-height: 145px;
         padding: 24px 26px;
       }
       .smart-checkout .single-project-card .choice-title { font-size: 18px; }
       .smart-checkout .plot-status { display: block; margin-top: 7px; }
       .smart-checkout .plot-status-checking { color: #8a6a00; }
       .smart-checkout .plot-status-valid { color: #16834b; }
       .smart-checkout .plot-status-invalid,
       .smart-checkout .plot-status-error { color: #d83a4b; }
       @media (max-width: 991.98px) {
          .smart-checkout .choice-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); }
          .smart-checkout .summary-card { position: static; }
        }
        @media (max-width: 575.98px) {
          .smart-checkout .checkout-shell { padding: 12px; border-radius: 16px; }
          .smart-checkout .hero-card { padding: 21px 18px; border-radius: 17px; }
          .smart-checkout .section-card, .smart-checkout .summary-card { padding: 16px; border-radius: 16px; }
          .smart-checkout .choice-grid { grid-template-columns: 1fr; gap: 10px; }
          .smart-checkout .choice-card { min-height: 100px; padding: 15px; }
        }
      `}</style>

      <div className="smart-checkout">
        <div className="checkout-shell">
          <div className="hero-card">
            <div className="hero-eyebrow">Land Booking</div>
            <h2 className="hero-title">Book your property with confidence</h2>
            <p className="hero-text">
              Select your project, plot type and payment method. Review the calculated booking details before confirmation.
            </p>
          </div>

          {!authToken && (
            <div className="login-warning">
              Your login session was not found. Please login before submitting a booking.
            </div>
          )}

          <form onSubmit={depositSubmit} noValidate>
            <div className="row">
              <div className="col-xl-8 col-lg-7">
                <div className="section-card">
                  <div className="section-head">
                    <span className="step-number">1</span>
                    <div>
                      <h4 className="section-title">Select Project</h4>
                      <div className="section-subtitle">Choose the project you want to book.</div>
                    </div>
                  </div>

                  {loadingProjects ? (
                    <div className="loading-box">Loading projects...</div>
                  ) : projects.length === 0 ? (
                    <div className="empty-box">No project is available right now.</div>
                  ) : (
                    <div className={`choice-grid ${projects.length === 1 ? "single-project-grid" : ""}`}>
                      {projects.map((item, index) => {
                        const active = String(selectedProductId) === String(item.id);
                        return (
                          <label className={`choice-card ${active ? "active" : ""} ${projects.length === 1 ? "single-project-card" : ""}`} key={item.id || index}>
                            <input
                              type="radio"
                              name="product_id"
                              value={item.id}
                              checked={active}
                              onChange={() => setSelectedProductId(item.id)}
                            />
                            <span className="choice-check">{active ? "✓" : ""}</span>
                            <span className="choice-index">{index + 1}</span>
                            <span className="choice-title">{item.project_name}</span>
                            <span className="choice-subtitle">Click to select project</span>
                          </label>
                        );
                      })}
                    </div>
                  )}
                  {errors.selectedProductId && <span className="field-error">{errors.selectedProductId}</span>}
                </div>

                {selectedProductId && (
                  <div className="section-card">
                    <div className="section-head">
                      <span className="step-number">2</span>
                      <div>
                        <h4 className="section-title">Type of Plot</h4>
                        <div className="section-subtitle">Select the available plot/package type.</div>
                      </div>
                    </div>

                    {loadingProjectTypes ? (
                      <div className="loading-box">Loading plot types...</div>
                    ) : projectTypes.length === 0 ? (
                      <div className="empty-box">No plot type is available for this project.</div>
                    ) : (
                      <div className="choice-grid">
                        {projectTypes.map((item, index) => {
                          const active = String(selectedTypeOfPlot) === String(item.id);
                          return (
                            <label className={`choice-card ${active ? "active" : ""}`} key={item.id || index}>
                              <input
                                type="radio"
                                name="type_of_plot"
                                value={item.id}
                                checked={active}
                                onChange={() => setSelectedTypeOfPlot(item.id)}
                              />
                              <span className="choice-check">{active ? "✓" : ""}</span>
                              <span className="choice-icon">⌂</span>
                              <span className="choice-title">{item.packege_name}</span>
                              <span className="choice-subtitle">Select plot type</span>
                            </label>
                          );
                        })}
                      </div>
                    )}
                    {errors.selectedTypeOfPlot && <span className="field-error">{errors.selectedTypeOfPlot}</span>}
                  </div>
                )}

                {selectedTypeOfPlot && (
                  <div className="section-card">
                    <div className="section-head">
                      <span className="step-number">3</span>
                      <div>
                        <h4 className="section-title">Land Quantity</h4>
                        <div className="section-subtitle">
                          Enter the number of kathas you want to acquire (minimum 3).
                        </div>
                      </div>
                    </div>
                    <div className="land-quantity-panel">
                      <label className="field-label">Land Area</label>
                      <div className="land-presets" role="group" aria-label="Choose land area">
                        {[3, 5, 9, 12, 15].map((qty) => (
                          <button
                            type="button"
                            key={qty}
                            className={`land-preset ${Number(landQuantity) === qty ? "active" : ""}`}
                            aria-pressed={Number(landQuantity) === qty}
                            disabled={submitting}
                            onClick={() => updateLandQuantity(qty)}
                          >
                            {qty} Kathas
                          </button>
                        ))}
                      </div>
                      <input type="hidden" name="land_quantity" value={landQuantity} />
                      <div className="land-hint">
                        Please select one of the five available land areas. Other quantities are not allowed.
                      </div>
                      {!validQuantity && (
                        <span className="field-error">
                          {errors.land_quantity || "Please select a valid land area."}
                        </span>
                      )}
                    </div>

                    <div className="mt-3">
                      <label className="field-label" htmlFor="plotNumber">
                        Plot Number <span className="text-muted fw-normal">(Optional)</span>
                      </label>
                      <input
                        id="plotNumber"
                        name="plot_number"
                        type="text"
                        className="smart-input"
                        placeholder="Enter plot number (optional)"
                        value={plotNumber}
                        maxLength={100}
                        disabled={submitting}
                        onChange={(e) => {
                          setPlotNumber(e.target.value);
                          setErrors((prev) => ({ ...prev, plot_number: undefined }));
                        }}
                        aria-describedby="plotNumberStatus"
                      />
                      {plotNumber.trim() && (
                        <span
                          id="plotNumberStatus"
                          className={`field-error plot-status plot-status-${plotCheckStatus}`}
                          role="status"
                        >
                          {plotCheckMessage}
                        </span>
                      )}
                      {errors.plot_number && <span className="field-error">{errors.plot_number}</span>}
                    </div>
                  </div>
                )}

                {selectedTypeOfPlot && (
                  <div className="section-card">
                    <div className="section-head">
                      <span className="step-number">4</span>
                      <div>
                        <h4 className="section-title">Payment Type</h4>
                        <div className="section-subtitle">Choose how you want to pay.</div>
                      </div>
                    </div>

                    <div className="choice-grid">
                      {paymentOptions.map((item) => {
                        const active = selectedPaymentType === item.value;
                        return (
                          <label className={`choice-card ${active ? "active" : ""}`} key={item.value}>
                            <input
                              type="radio"
                              name="payment_type"
                              value={item.value}
                              checked={active}
                              onChange={() => setSelectedPaymentType(item.value)}
                            />
                            <span className="choice-check">{active ? "✓" : ""}</span>
                            <span className="choice-icon">{item.icon}</span>
                            <span className="choice-title">{item.title}</span>
                            <span className="choice-subtitle">{item.subtitle}</span>
                          </label>
                        );
                      })}
                    </div>
                    {errors.selectedPaymentType && <span className="field-error">{errors.selectedPaymentType}</span>}
                  </div>
                )}

                {selectedPaymentType === "installment" && (
                  <div className="section-card">
                    <div className="section-head">
                      <span className="step-number">5</span>
                      <div>
                        <h4 className="section-title">Type of Installment</h4>
                        <div className="section-subtitle">Choose your preferred installment schedule.</div>
                      </div>
                    </div>

                    {loadingPaymentData && installmentTypes.length === 0 ? (
                      <div className="loading-box">Loading installment plans...</div>
                    ) : installmentTypes.length === 0 ? (
                      <div className="empty-box">No installment plan is available.</div>
                    ) : (
                      <div className="choice-grid">
                        {installmentTypes.map((item, index) => {
                          const active = String(selectedInstallment) === String(item.id);
                          return (
                            <label className={`choice-card ${active ? "active" : ""}`} key={item.id || index}>
                              <input
                                type="radio"
                                name="type_installment"
                                value={item.id}
                                checked={active}
                                onChange={() => setSelectedInstallment(item.id)}
                              />
                              <span className="choice-check">{active ? "✓" : ""}</span>
                              <span className="choice-icon">{index + 1}</span>
                              <span className="choice-title">{item.installment_name}</span>
                              <span className="choice-subtitle">Select installment plan</span>
                            </label>
                          );
                        })}
                      </div>
                    )}
                    {errors.selectedsetInstallment && (
                      <span className="field-error">{errors.selectedsetInstallment}</span>
                    )}
                  </div>
                )}

                <div className="section-card">
                  <div className="section-head">
                    <span className="step-number">{selectedPaymentType === "installment" ? "6" : "5"}</span>
                    <div>
                      <h4 className="section-title">Confirm Booking</h4>
                      <div className="section-subtitle">Verify the amount and enter your account password.</div>
                    </div>
                  </div>

                  <div className="row">
                    <div className="col-md-4 mb-3">
                      <label className="field-label" htmlFor="bookingMoney">Booking Money</label>
                      <input id="bookingMoney" name="booking_money" type="text" className="smart-input"
                        value={`${money(calculatedSummary.booking_fee)} TK`} readOnly />
                      {errors.booking_money && <span className="field-error">{errors.booking_money}</span>}
                    </div>
                    <div className="col-md-4 mb-3">
                      <label className="field-label" htmlFor="totalAmount">Total Amount</label>
                      <input id="totalAmount" name="total_amount" type="text" className="smart-input"
                        value={`${money(calculatedSummary.installment_total)} TK`} readOnly />
                      {errors.total_amount && <span className="field-error">{errors.total_amount}</span>}
                    </div>
                    <div className="col-md-4 mb-3">
                      <label className="field-label" htmlFor="installmentAmount">Installment Amount</label>
                      <input id="installmentAmount" name="installment_amount" type="text" className="smart-input"
                        value={`${money(calculatedSummary.installment_amount)} TK`} readOnly />
                      {errors.installment_amount && <span className="field-error">{errors.installment_amount}</span>}
                    </div>
                    <div className="col-md-6 mb-3">
                      <label className="field-label" htmlFor="accountPassword">Account Password</label>
                      <div className="password-wrap">
                        <input
                          id="accountPassword"
                          type={showPassword ? "text" : "password"}
                          className="smart-input"
                          placeholder="Enter account password"
                          name="password"
                          autoComplete="current-password"
                          value={password}
                          onChange={(e) => {
                            setPassword(e.target.value);
                            if (errors.password) setErrors((prev) => ({ ...prev, password: undefined }));
                          }}
                        />
                        <button
                          type="button"
                          className="password-toggle"
                          onClick={() => setShowPassword((prev) => !prev)}
                        >
                          {showPassword ? "Hide" : "Show"}
                        </button>
                      </div>
                      {errors.password && <span className="field-error">{errors.password}</span>}
                    </div>
                  </div>

                  <div className="terms-box">
                    <input
                      type="checkbox"
                      id="acceptTerms"
                      checked={acceptedTerms}
                      onChange={(e) => {
                        setAcceptedTerms(e.target.checked);
                        if (errors.terms) setErrors((prev) => ({ ...prev, terms: undefined }));
                      }}
                    />
                    <label htmlFor="acceptTerms">
                      I confirm that I have reviewed the booking details and accept the terms &amp; conditions.
                    </label>
                  </div>
                  {errors.terms && <span className="field-error mb-3">{errors.terms}</span>}

                  <button className="submit-btn" type="submit" disabled={!canSubmit || submitting}>
                    {submitting ? (
                      <span className="d-inline-flex align-items-center justify-content-center">
                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                        Submitting Booking...
                      </span>
                    ) : "Continue Booking"}
                  </button>
                </div>
              </div>

              <div className="col-xl-4 col-lg-5">
                <div className="summary-card">
                  <h4 className="summary-title">Booking Summary</h4>
                  <div className="summary-subtitle">
                    Your calculated booking information will appear here.
                  </div>

                  <div className="summary-row">
                    <span className="summary-label">Land Quantity</span>
                    <span className="summary-value">{validQuantity ? landQuantity : "—"} Kathas</span>
                  </div>
                  <div className="summary-row">
                    <span className="summary-label">Price Basis</span>
                    <span className="summary-value">Per Katha × {validQuantity ? landQuantity : "—"}</span>
                  </div>
                  <div className="summary-row">
                    <span className="summary-label">Booking Money</span>
                    <span className="summary-value">{money(calculatedSummary.booking_fee)} TK</span>
                  </div>
                  <div className="summary-row">
                    <span className="summary-label">Total Amount</span>
                    <span className="summary-value">{money(calculatedSummary.installment_total)} TK</span>
                  </div>
                  <div className="summary-row">
                    <span className="summary-label">Installment Amount</span>
                    <span className="summary-value">{money(calculatedSummary.installment_amount)} TK</span>
                  </div>
                  <div className="summary-row">
                    <span className="summary-label">Currency</span>
                    <span className="summary-value">{selectedCurrency}</span>
                  </div>

                  <div className="payable-box">
                    <div>
                      <small>Payable now</small>
                      <span>Booking amount</span>
                    </div>
                    <strong>{money(payableAmount)} TK</strong>
                  </div>

                  {loadingPaymentData && (
                    <div className="loading-box mt-3">Calculating payment details...</div>
                  )}
                </div>
              </div>
            </div>
          </form>
        </div>
      </div>
    </Fragment>
  );
};

export default Checkout;
